import {
  CellValue,
  ChannelId,
  HeatMode,
  resolveHigherIsBetter,
  resolveTableHeatMode,
  TableColumn
} from '@blisspointmedia/bpm-types/dist/dashboarding';
import { getStandardDeviation } from '@blisspointmedia/bpm-types/dist/brandHealthMetricsUtils';
import {
  DivergentColorMap20,
  DivergentGreenDark,
  HeatMap,
  makeSteppedHeatMap,
  SequentialGreenRamp
} from '@src/utils/colors';

/**
 * The NUMERIC half of table heat mapping: a column's color domain, and the color one cell's value
 * lands on within it. The policy half -- whether a column shades at all, with which scheme, and
 * which direction is better -- lives in bpm-types (`dashboarding/heatMap.ts`) next to the registry
 * it reads, so nothing here carries a list of which columns shade.
 *
 * Color comes from `makeSteppedHeatMap` (`src/utils/colors.ts`), the app's shared heat-map scale factory,
 * rather than a bespoke ramp: it already owns the midpoint split, the clamp to [min, max], the
 * zero-width-arm guard, and per-value memoization, and it is what CreativeTray, the MMM channel
 * table, CategoryPathing and AudiencePenetration all use. So this file owns only what is specific
 * to dashboarding tables: the clipped domain, the direction flip, and the midpoint hairline.
 */

/**
 * A column's color domain. `lo`/`hi` are CLIPPED to one standard deviation either side of the mean
 * (never wider than the real min/max). That clip meaningfully helps a MODERATE tail -- an outlier
 * only a few times the size of the rest of the column -- by re-spreading the bulk of the values
 * across more of the ramp instead of leaving them crowded at one end.
 *
 * For a DOMINANT outlier -- one order of magnitude or more above the rest -- the clip is close to a
 * no-op: `hi` still lands far above every non-outlier value, so those values still saturate onto the
 * same palest step and the column reads near-flat. See the "σ-clip effect on ramp spread" tests in
 * `table-heatmap-utils.test.ts` for both cases side by side.
 *
 * This was accepted deliberately, not missed: it matches `MetricsTable`'s default
 * `stdevClipping: 1` (see `metrics-table-utils.ts`, which computes the same band), and parity with
 * that existing behavior is the requirement here, not a wider clip. `revenue` -- shaded by the
 * registry on most channels -- is exactly the column this shows up on in practice, since it's
 * typically dominated by one platform: a near-flat Revenue column is the expected real-world
 * outcome, not a bug to chase.
 */
export interface HeatDomain {
  lo: number;
  hi: number;
  mean: number;
}

/**
 * The clipped domain for one column's values, or `null` when there is nothing to shade -- an empty
 * or wholly non-numeric column, a constant column, or a single row. Returning `null` for a
 * zero-width domain is what lets every caller below assume `lo < hi`.
 *
 * `getStandardDeviation` (bpm-types) is the shared population-stdev helper, so the variance math
 * lives in one place rather than being re-derived here.
 */
export const computeHeatDomain = (values: CellValue[]): HeatDomain | null => {
  const numbers = values.filter(
    (value): value is number => typeof value === 'number' && Number.isFinite(value)
  );
  if (numbers.length === 0) return null;

  const mean = numbers.reduce((sum, value) => sum + value, 0) / numbers.length;
  const stdev = getStandardDeviation(numbers);
  // An explicit fold rather than `Math.min(...numbers)`: this runs over EVERY fetched row, and
  // spreading a large column into an argument list would risk a call-stack overflow.
  let min = Infinity;
  let max = -Infinity;
  for (const value of numbers) {
    if (value < min) {
      min = value;
    }
    if (value > max) {
      max = value;
    }
  }

  const lo = Math.max(min, mean - stdev);
  const hi = Math.min(max, mean + stdev);
  return lo === hi ? null : { lo, hi, mean };
};

/** What a shaded cell paints: a background, plus an inset hairline where the step needs one. */
export interface HeatStyle {
  backgroundColor: string;
  ring?: string;
}

/**
 * The hairline on a cell sitting essentially at the midpoint. The divergent ramp's innermost stops
 * are very pale, so without this a near-anchor cell reads as unshaded. Carried as an 8-digit alpha
 * value, which is fine because it is used directly as a CSS color and never parsed by `hexToRgb`.
 */
const DIVERGING_RING_COLOR = `${DivergentGreenDark}80`;

/**
 * How close to the midpoint a diverging cell must sit, as a share of its arm's width, to earn the
 * hairline. Matches `MetricsTable`'s own threshold (`metrics-table-utils.ts`, `pct < 0.05`).
 */
const RING_THRESHOLD = 0.05;

interface HeatStyleInput {
  mode: HeatMode;
  domain: HeatDomain;
  higherIsBetter: boolean;
  /** Where the diverging scale's neutral pole sits. Ignored by the sequential scale. */
  midpoint: number;
}

/**
 * Magnitude scale: lightest at one end of the domain, darkest at the other, flipping which end is
 * dark when lower is better. Same ramp CreativeTray feeds (`creativeTrayUtils.ts`), so the two read
 * identically.
 */
const sequentialScale = ({ lo, hi }: HeatDomain, higherIsBetter: boolean): HeatMap =>
  makeSteppedHeatMap({
    min: lo,
    max: hi,
    // The ramp runs light -> dark, so it maps min -> max as authored when higher is better and
    // needs reversing when the low end of the domain is the desirable one.
    stops: higherIsBetter ? SequentialGreenRamp : [...SequentialGreenRamp].reverse()
  });

/**
 * Polarity scale: green at the favorable end of the domain, red at the unfavorable one, with the
 * two arms meeting at their palest stops on the midpoint. `DivergentColorMap20` is authored worst
 * -> best, which is min -> max only when higher is better; a cost metric reverses it so that the
 * low end of the domain shades green.
 */
const divergingScale = (
  { lo, hi }: HeatDomain,
  midpoint: number,
  higherIsBetter: boolean
): HeatMap =>
  makeSteppedHeatMap({
    min: lo,
    max: hi,
    midpoint,
    stops: higherIsBetter ? DivergentColorMap20 : [...DivergentColorMap20].reverse()
  });

/**
 * How far along its arm a value sits, 0 at the midpoint and 1 at that arm's bound.
 * `makeSteppedHeatMap` computes the same ratio internally but returns only a color, so the ring
 * decision needs its own read.
 */
const midpointProximity = (value: number, { lo, hi }: HeatDomain, midpoint: number): number => {
  const span = value > midpoint ? hi - midpoint : midpoint - lo;
  // A zero-width arm reports 1, not 0: `makeSteppedHeatMap` saturates such a value to that arm's
  // pole (`pickStop`'s `hi <= lo` branch returns the last stop), so reporting "at the pole" keeps
  // the ring decision consistent with the color instead of ringing a fully-saturated cell.
  if (span <= 0) return 1;
  return Math.min(1, Math.abs(value - midpoint) / span);
};

/**
 * Bind a column's scheme, domain and direction into a per-cell style function. Returns `null` for a
 * cell that has no number to shade -- a `null`, a string, a `NaN` -- which is distinct from the
 * column not being shaded at all (bpm-types' `resolveTableHeatMode` answers that, once, upstream).
 *
 * The scale is built ONCE per column here, not per cell; `makeSteppedHeatMap` then memoizes per
 * value on top of that.
 */
export const buildHeatStyle = ({ mode, domain, higherIsBetter, midpoint }: HeatStyleInput) => {
  const scale =
    mode === 'sequential'
      ? sequentialScale(domain, higherIsBetter)
      : divergingScale(domain, midpoint, higherIsBetter);

  return (value: CellValue): HeatStyle | null => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return null;
    const backgroundColor = scale(value);
    if (mode === 'sequential') return { backgroundColor };
    return midpointProximity(value, domain, midpoint) < RING_THRESHOLD
      ? { backgroundColor, ring: DIVERGING_RING_COLOR }
      : { backgroundColor };
  };
};

interface ColumnHeatInput {
  col: TableColumn;
  /** EVERY fetched value for this column -- not the `rowLimit` slice, and not the search result. */
  values: CellValue[];
  isMetric: boolean;
  isDate: boolean;
  /** The channel this column's query reads; without it the registry can't be consulted. */
  channel?: ChannelId;
}

/**
 * The one entry point `prepareTable` calls: ask bpm-types whether and how this column shades, then
 * bind a per-cell style function over its domain. `undefined` means the column renders plain.
 *
 * The domain is computed from every FETCHED row, once, at prep time. That is what keeps colors
 * stable while the user searches: narrowing the displayed rows re-renders the body but never
 * re-scales the ramp, so a surviving row keeps the color it had. Color follows the value, not its
 * rank among whatever is currently on screen.
 */
export const buildColumnHeatStyle = ({
  col,
  values,
  isMetric,
  isDate,
  channel
}: ColumnHeatInput): ((value: CellValue) => HeatStyle | null) | undefined => {
  const mode = resolveTableHeatMode({ col, isMetric, isDate, channel });
  if (!mode) return undefined;
  const domain = computeHeatDomain(values);
  if (!domain) return undefined;
  // A derived (this-vs-prior %Δ) column has no `ref` -- it carries `col.derived = { op, of, vs }`
  // instead. Fall back to `of`, the CURRENT-period ref: a delta's polarity follows its base
  // measure's direction for every `op` (percentDelta, absoluteDelta, ratio alike), since `of` is
  // the metric actually being compared. Without this fallback, `resolveHigherIsBetter(undefined)`
  // defaults to `true`, which paints a cost metric's %Δ increase green -- a cost blowout reading as
  // good. Do not "simplify" this back to `col.ref`.
  return buildHeatStyle({
    mode,
    domain,
    higherIsBetter: resolveHigherIsBetter(col.ref ?? col.derived?.of),
    midpoint: domain.mean
  });
};
